import { useEffect, useState } from "react";
import { TrendingUp, PlugZap } from "lucide-react";
import { BarChart, Bar, LineChart, Line, Legend, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, aud, errMsg } from "../../lib/api";
import { useGet } from "../../lib/hooks";
import { Stat } from "../Common";
import { METRICS } from "./Widgets";
import { LinksDirectory } from "../links/LinksDirectory";

export const LIB_TYPES = {
  financial_overview: { label: "Financial overview (revenue, expenses, net result + chart)", source: "Xero" },
  revenue_growth: { label: "Revenue growth by financial year", source: "Xero" },
  stat_cards: { label: "Stat cards (BOS HQ live counters)", source: "BOS HQ" },
  bar_chart: { label: "Pipeline by stage chart (BOS HQ)", source: "BOS HQ" },
  important_links: { label: "Important links directory (BOS HQ)", source: "BOS HQ" },
};
const PERIODS = [["this_month", "This Month"], ["last_month", "Last Month"], ["this_quarter", "This Quarter"], ["last_quarter", "Last Quarter"], ["fytd", "FYTD"], ["prev_fy", "Previous FY"], ["custom", "Custom"]];
const COMPARE = [["previous", "Previous period", "vs previous period"], ["last_year", "Same period last year", "vs same period last year"], ["none", "No comparison", ""]];
const fmtD = (s) => s ? s.split("-").reverse().join("/") : "";
const shell = "rounded-xl border border-white/10 bg-[#0f1a2b] p-5";

const fyStart = (d = new Date()) => (d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1);

function ConnectNote({ source, testid }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-8 gap-2" data-testid={testid}>
      <PlugZap className="h-5 w-5 text-[#C2A462]" />
      <div className="text-sm font-semibold text-slate-200">Connect {source} to show live figures</div>
      <div className="text-xs text-slate-500">An administrator can connect it in Integrations. Need help? Contact <a href="mailto:support@boshq.com.au" className="text-[#C2A462] hover:underline">support@boshq.com.au</a></div>
    </div>
  );
}

function useXero(path) {
  const [st, setSt] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let on = true;
    api.get("/xero/status").then(({ data: s }) => {
      if (!on) return; setSt(s);
      if (s.connected && path) { setData(null); setErr(null); api.get(path).then((r) => on && setData(r.data)).catch((e) => on && setErr(e.response?.status === 403 ? "You don't have access to financial figures." : errMsg(e))); }
    }).catch(() => on && setSt({ connected: false }));
    return () => { on = false; };
  }, [path]);
  return { st, data, err };
}

const pct = (a, b) => (b ? ((a - b) / Math.abs(b)) * 100 : null);
function Move({ cur, prev, invert, label }) {
  const p = pct(cur, prev);
  if (p === null) return <div className="text-xs text-slate-500">No comparison</div>;
  const good = invert ? cur <= prev : cur >= prev;
  return <div className={`text-xs ${good ? "text-emerald-400" : "text-red-400"}`}>{cur >= prev ? "▲" : "▼"} {aud(Math.abs(cur - prev))} · {Math.abs(p).toFixed(1)}% <span className="text-slate-400">{label}</span></div>;
}

function CustomRange({ id, onApply, compare, setCompare }) {
  const [f, setF] = useState(""), [t, setT] = useState("");
  const cls = "h-9 w-full rounded-md border border-white/10 bg-black/30 px-2 text-sm text-slate-100";
  return (
    <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_1.4fr_auto] gap-3 items-end rounded-lg border border-white/10 bg-black/20 p-3" data-testid={`lib-custom-${id}`}>
      <label className="text-[11px] uppercase tracking-[0.15em] text-slate-400">From<input type="date" className={cls} value={f} onChange={(e) => setF(e.target.value)} data-testid={`lib-from-${id}`} /></label>
      <label className="text-[11px] uppercase tracking-[0.15em] text-slate-400">To<input type="date" className={cls} value={t} onChange={(e) => setT(e.target.value)} data-testid={`lib-to-${id}`} /></label>
      <CompareSelect id={id} compare={compare} setCompare={setCompare} />
      <button disabled={!f || !t || t < f} onClick={() => onApply(f, t)} data-testid={`lib-apply-${id}`}
        className="h-9 px-6 rounded-md bg-[#C2A462] text-[#111] font-semibold text-sm disabled:opacity-40 transition-opacity">Apply</button>
    </div>
  );
}

function CompareSelect({ id, compare, setCompare }) {
  return (
    <label className="text-[11px] uppercase tracking-[0.15em] text-slate-400">Compare
      <select className="h-9 w-full rounded-md border border-[#C2A462]/40 bg-black/30 px-2 text-sm text-slate-100" value={compare} onChange={(e) => setCompare(e.target.value)} data-testid={`lib-compare-${id}`}>
        {COMPARE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </label>
  );
}

function ComparisonBars({ c, p, compare }) {
  const name = COMPARE.find((x) => x[0] === compare)[1];
  const rows = [["Revenue", "revenue"], ["Expenses", "expenses"], ["Net result", "net"]].map(([l, k]) => ({ name: l, Selected: c[k], ...(p ? { [name]: p[k] } : {}) }));
  return (
    <div className="h-56 mt-3"><ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
      <BarChart data={rows}><CartesianGrid strokeDasharray="3 3" stroke="hsl(40 18% 14%)" /><XAxis dataKey="name" stroke="#94a3b8" fontSize={11} /><YAxis stroke="#94a3b8" fontSize={11} />
        <Tooltip contentStyle={{ background: "#0b1428", border: "1px solid #C2A46255" }} formatter={(v) => aud(v)} cursor={{ fill: "rgba(194,164,98,0.06)" }} /><Legend />
        <Bar dataKey="Selected" fill="#C2A462" radius={[4, 4, 0, 0]} />{p && <Bar dataKey={name} fill="#64748b" radius={[4, 4, 0, 0]} />}</BarChart>
    </ResponsiveContainer></div>
  );
}

function FinancialOverview({ def }) {
  const [period, setPeriod] = useState("fytd");
  const [compare, setCompare] = useState("previous");
  const [range, setRange] = useState(null);
  const path = period === "custom" ? (range ? `/xero/financials?period=custom&start=${range[0]}&end=${range[1]}&compare=${compare}` : null) : `/xero/financials?period=${period}&compare=${compare}`;
  const { st, data, err } = useXero(path);
  const c = data?.current, p = data?.previous;
  const clabel = COMPARE.find((x) => x[0] === compare)[2];
  const cards = [["Revenue", "revenue", "bg-emerald-400"], ["Expenses", "expenses", "bg-amber-400", true], ["Net result", "net", "bg-indigo-400"]];
  return (
    <div className={shell} data-testid={`lib-widget-${def.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-lg font-bold text-slate-100"><TrendingUp className="h-5 w-5 text-emerald-400" />{def.name}</div>
          {def.subtitle && <div className="text-xl font-bold text-white mt-1">{def.subtitle}</div>}
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg border border-white/10 p-1">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`lib-period-${def.id}-${k}`}
              className={`px-3 py-1 rounded-md text-xs transition-colors ${period === k ? "bg-[#C2A462]/15 text-[#EBD6A0] border border-[#C2A462]/40" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      {period === "custom" ? <CustomRange id={def.id} compare={compare} setCompare={setCompare} onApply={(a, b) => setRange([a, b])} />
        : <div className="mt-4 max-w-xs"><CompareSelect id={def.id} compare={compare} setCompare={setCompare} /></div>}
      {err && <div className="mt-4 text-sm text-red-400" data-testid={`lib-error-${def.id}`}>{err}</div>}
      <div className="grid gap-3 mt-5 grid-cols-[repeat(auto-fit,minmax(130px,1fr))]">
        {cards.map(([l, k, dot, inv]) => (
          <div key={k} className="min-w-0 overflow-hidden rounded-lg border border-white/10 bg-black/20 p-3">
            <div className="flex justify-between gap-1 text-[10px] uppercase tracking-[0.12em] text-slate-400"><span className="truncate">{l}</span><span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} /></div>
            <div className="mt-1.5 truncate text-base font-bold text-slate-100 sm:text-lg" data-testid={`lib-${k}-${def.id}`}>{c ? aud(c[k]) : "—"}</div>
            {c ? (p ? <Move cur={c[k]} prev={p[k]} invert={inv} label={clabel} /> : <div className="text-xs text-slate-500">No comparison</div>) : <div className="text-xs text-slate-500">{st?.connected ? (period === "custom" && !range ? "Choose dates and click Apply" : "Loading…") : "Not connected"}</div>}
          </div>
        ))}
        <div className="min-w-0 overflow-hidden rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="truncate text-[10px] uppercase tracking-[0.12em] text-slate-400">Net margin</div>
          <div className="mt-1.5 truncate text-base font-bold text-slate-100 sm:text-lg">{c?.margin != null ? `${c.margin}%` : "—"}</div>
          {p?.margin != null && <div className="text-xs text-slate-500">Comparison {p.margin}%</div>}
        </div>
      </div>
      {c && (
        <div className="rounded-lg border border-white/10 bg-black/20 p-4 mt-3" data-testid={`lib-compare-chart-${def.id}`}>
          <div className="text-sm font-semibold text-slate-200">Selected period comparison</div>
          <div className="text-xs text-slate-500">{fmtD(data.from)} – {fmtD(data.to)}{p ? ` compared with ${fmtD(data.prev_from)} – ${fmtD(data.prev_to)}` : ""}</div>
          {period === "custom" && <ComparisonBars c={c} p={p} compare={compare} />}
        </div>
      )}
      <div className="rounded-lg border border-white/10 bg-black/20 p-4 mt-3">
        <div className="text-sm font-semibold text-slate-200">Revenue vs Expenses</div>
        <div className="text-xs text-slate-500">Last 12 months · AUD, GST-exclusive · accrual basis</div>
        {data ? (
          <div className="h-56 mt-3"><ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
            <LineChart data={data.monthly}><CartesianGrid strokeDasharray="3 3" stroke="hsl(40 18% 14%)" /><XAxis dataKey="month" stroke="#94a3b8" fontSize={11} /><YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0b1428", border: "1px solid #C2A46255" }} formatter={(v) => aud(v)} /><Legend />
              <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#34d399" strokeWidth={2} dot={false} /><Line type="monotone" dataKey="expenses" name="Expenses" stroke="#fbbf24" strokeWidth={2} dot={false} /></LineChart>
          </ResponsiveContainer></div>
        ) : !st?.connected && st && <ConnectNote source="Xero" testid={`lib-connect-${def.id}`} />}
        <div className="text-right text-[11px] text-slate-500">{data ? `Live from Xero · ${data.from} to ${data.to}` : "Live from Xero"}</div>
      </div>
    </div>
  );
}

function RevenueGrowth({ def }) {
  const start = Number(def.config?.established_fy) || fyStart();
  const now = fyStart();
  const { st, data, err } = useXero(`/xero/revenue-by-fy?start_fy=${start}`);
  const byFy = Object.fromEntries((data?.years || []).map((y) => [y.fy, y.revenue]));
  const years = [];
  for (let y = start; y <= now; y++) years.push(y);
  return (
    <div className={shell} data-testid={`lib-widget-${def.id}`}>
      <div className="flex justify-between"><div className="text-lg font-bold text-slate-100">{def.name}</div><span className="text-[10px] text-slate-500">Xero</span></div>
      <div className="text-xs text-slate-400">{def.subtitle || `Since establishment · ${start}–present`}</div>
      {err && <div className="mt-2 text-xs text-red-400">{err}</div>}
      <div className="mt-4 space-y-3 border-l border-white/10 pl-4">
        {years.map((y) => {
          const prev = byFy[y - 1], cur = byFy[y];
          return (
            <div key={y}><div className="text-xs font-semibold tracking-wide text-slate-300">FY {y}–{String(y + 1).slice(2)} {y === now && <span className="text-[#C2A462]">YTD</span>}</div>
              <div className="text-slate-100 font-semibold" data-testid={`lib-fy-${def.id}-${y}`}>{cur != null ? aud(cur) : "—"}</div>
              {cur != null && prev ? <div className={`text-[11px] ${cur >= prev ? "text-emerald-400" : "text-red-400"}`}>{cur >= prev ? "▲" : "▼"} {Math.abs(pct(cur, prev)).toFixed(1)}% on previous year</div> : null}
            </div>
          );
        })}
      </div>
      {st && !st.connected && <div className="mt-3 text-[11px] text-slate-500">Connect Xero to show revenue history. Need help? support@boshq.com.au</div>}
    </div>
  );
}

function StatCards({ def, d }) {
  return (
    <div data-testid={`lib-widget-${def.id}`}>
      <div className="text-sm font-semibold text-slate-200 mb-2">{def.name}</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {(def.config?.metrics || []).filter((m) => METRICS[m]).map((m) => { const [label, icon, f] = METRICS[m]; return <Stat key={m} label={label} icon={icon} value={d ? (f ? f(d[m]) : d[m]) : "…"} />; })}
      </div>
    </div>
  );
}

function StageChart({ def, d }) {
  return (
    <div className={shell} data-testid={`lib-widget-${def.id}`}>
      <div className="text-sm font-semibold text-slate-200">{def.name}</div>
      {def.subtitle && <div className="text-xs text-slate-500">{def.subtitle}</div>}
      <div className="h-56 mt-3"><ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
        <BarChart data={d?.by_stage || []}><CartesianGrid strokeDasharray="3 3" stroke="hsl(40 18% 14%)" /><XAxis dataKey="stage" stroke="#94a3b8" fontSize={11} /><YAxis stroke="#94a3b8" fontSize={11} />
          <Tooltip contentStyle={{ background: "#0b1428", border: "1px solid #C2A46255" }} formatter={(v) => aud(v)} /><Bar dataKey="value" fill="#C2A462" radius={[3, 3, 0, 0]} /></BarChart>
      </ResponsiveContainer></div>
    </div>
  );
}

export function LibraryWidgetView({ def, d }) {
  const C = { financial_overview: FinancialOverview, revenue_growth: RevenueGrowth, stat_cards: StatCards, bar_chart: StageChart, important_links: () => <LinksDirectory compact /> }[def.type];
  return C ? <C def={def} d={d} /> : null;
}

export function LibraryWidget({ c, d }) {
  const [lib] = useGet("/widget-library");
  if (!lib) return null;
  const def = lib.find((x) => x.id === c.widget_def_id);
  if (!def) return <div className={`${shell} text-sm text-slate-400`} data-testid="lib-widget-unavailable">This widget isn't included in your organisation's plan.</div>;
  return <LibraryWidgetView def={def} d={d} />;
}
